import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api, { formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { Mail, ShieldCheck, Lock, ArrowLeft, Check, X } from 'lucide-react';

const COMPLEXITY = [
  { test: (p) => p.length >= 8, label: 'At least 8 characters' },
  { test: (p) => /[A-Z]/.test(p), label: 'One uppercase letter' },
  { test: (p) => /[0-9]/.test(p), label: 'One number' },
  // eslint-disable-next-line no-useless-escape
  { test: (p) => /[!@#\$%\^&\*\(\)_\+\-=\[\]\{\};:'",.<>\/?\\|`~]/.test(p), label: 'One special character (!@#$ etc.)' },
];

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1=email, 2=otp+new-pw
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busy, setBusy] = useState(false);

  const sendOtp = async () => {
    if (!email.trim()) return toast.error('Enter your work email');
    setBusy(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      toast.success('If the email is registered, a code has been sent. Check your inbox.');
      setStep(2);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (code.length !== 6) return toast.error('Enter the 6-digit code from your email');
    if (newPw !== confirmPw) return toast.error('Passwords do not match');
    const failed = COMPLEXITY.find(c => !c.test(newPw));
    if (failed) return toast.error(failed.label + ' is required');
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { email: email.trim(), code: code.trim(), new_password: newPw });
      toast.success('Password reset — please log in with your new password');
      nav('/login');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          <Link to="/login" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-[#3D4A8E] mb-6" data-testid="back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </Link>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-lg bg-[#3D4A8E] flex items-center justify-center overflow-hidden p-1">
              <img src="/lanvaus-logo.png" alt="Lanvaus" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-heading font-bold text-lg tracking-tight text-slate-900">LANVAUS</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Forgot password</div>
            </div>
          </div>

          {step === 1 && (
            <>
              <h1 className="font-heading text-3xl font-bold text-slate-900">Reset your password</h1>
              <p className="text-slate-500 mt-2 text-sm">We'll send a 6-digit code to your work email.</p>
              <div className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Work email</Label>
                  <div className="relative mt-1">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                           onKeyDown={(e) => e.key === 'Enter' && sendOtp()} className="pl-9 rounded-xl h-11" autoFocus />
                  </div>
                </div>
                <Button data-testid="send-otp-button" onClick={sendOtp} disabled={busy} className="w-full rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] h-11">
                  {busy ? 'Sending…' : 'Send code'}
                </Button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="font-heading text-3xl font-bold text-slate-900">Enter the code</h1>
              <p className="text-slate-500 mt-2 text-sm">Sent to <b>{email}</b>. Code expires in 15 minutes.</p>
              <div className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">6-digit code</Label>
                  <div className="relative mt-1">
                    <ShieldCheck className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input data-testid="reset-otp-input" inputMode="numeric" maxLength={6}
                           value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                           className="pl-9 rounded-xl h-11 tracking-[0.4em] text-center font-bold" autoFocus />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">New password</Label>
                  <div className="relative mt-1">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input data-testid="reset-new-password" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="pl-9 rounded-xl h-11" />
                  </div>
                  <div className="mt-2 space-y-1" data-testid="reset-complexity-checklist">
                    {COMPLEXITY.map((c, i) => {
                      const ok = c.test(newPw);
                      return (
                        <div key={i} className={`text-xs flex items-center gap-1.5 ${ok ? 'text-emerald-700' : 'text-slate-400'}`}>
                          {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {c.label}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Confirm new password</Label>
                  <Input data-testid="reset-confirm-password" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="rounded-xl h-11 mt-1" />
                </div>
                <Button data-testid="reset-submit-button" onClick={reset} disabled={busy} className="w-full rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] h-11">
                  {busy ? 'Resetting…' : 'Reset password'}
                </Button>
                <button onClick={() => { setStep(1); setCode(''); setNewPw(''); setConfirmPw(''); }}
                        data-testid="forgot-resend" className="text-xs text-[#3D4A8E] hover:underline w-full text-center">
                  Didn't get the code? Try again
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
